import { Message } from '../chatbot/chat/message';
import { Chat } from '../chatbot/chat/chat';
import { LLMAPI, Role, IChatInfo, BillingRecords, ChatError, TokenPrices } from '../chatbot/api/llmapi';
import { TigerAPI } from '../chatbot/api/tigerapi';
import { ModelList } from './modellist';
import { TrainDialog } from '../dialogs/traindialog';
import { Util } from './util';

type ThreadUpdatedCallback = () => void;

export class Conversation {
    public static chat         : Chat | undefined;
    public static lastMessage  : Message | undefined;
    static loading      : boolean   = false;
    static api          : LLMAPI = new TigerAPI(Util.getTigerAPIUrl());
    static listOfChats  : IChatInfo[] | undefined;
    static callbacks    : Set<ThreadUpdatedCallback> = new Set<ThreadUpdatedCallback>();
    static bills        : BillingRecords = { bills: [] };
    static userCredits  : number = 0;
    static tokenPricing : TokenPrices;
    static creditListeners: ((credit: number) => void)[] = [];


    static addMessage(msg : Message) : void
    {
        if (Conversation.chat === undefined) {
            Conversation.chat = new Chat(msg);
        }
        else {
            Conversation.chat.addReply(msg);
        }
    }

    static forceUpdate(lastMessage? : Message) 
    {
        Conversation.lastMessage = lastMessage;
        Conversation.callbacks.forEach(callback => {
            callback();
        });
    }

    static getMessages() : Message[]
    {
        if (Conversation.chat === undefined) {
            return [];
        }
        else {
            return Conversation.chat.getMessages();
        }
    }

    static async getReply(messages : Message[]) : Promise<void> 
    {
        let hasAddedReply = false;

        try {
            Conversation.setLoading(true);

            if (ModelList.selectedModel === undefined) {
                alert("Select model first");
                return;
            }

            if (Conversation.api.getReplyStreaming) {
                const last  = messages[messages.length - 1];
                const reply = new Message(Role.Assistant, "");
                last.addReply(reply);
                hasAddedReply = true;
                Conversation.forceUpdate();

                // Send message to API and get response
                reply.showCursor = true;
                await Conversation.api.getReplyStreaming(ModelList.selectedModel, messages, (part : string) => { reply.content += part; Conversation.forceUpdate(reply); });
                reply.showCursor = false;
                Conversation.forceUpdate(reply);
            }
            else {
                Conversation.forceUpdate();

                // Send message to API and get response
                const reply = await Conversation.api.getReply(ModelList.selectedModel, messages);

                const last = messages[messages.length - 1];
                last.addReply(reply);
                hasAddedReply = true;
                Conversation.forceUpdate(reply);
            }
        }
        catch (error) {
            const last  = messages[messages.length - 1];
            let reply: Message;

            if (error instanceof ChatError) {
                reply = error.getMessage();
            } else {
                reply = new Message(Role.Assistant, 'Sorry, I encountered an error processing your request.');
            }

            if (hasAddedReply) {
                last.replaceReply(reply);
            }
            else {
                last.addReply(reply);
            }
        }
        finally {
            Conversation.setLoading(false);
            Conversation.forceUpdate();
            Conversation.fetchUserCredits(true);
        }
    }

    static onUpdate(updateHandler : ThreadUpdatedCallback)
    {
        Conversation.callbacks.add(updateHandler);
    }

    static removeUpdateEvent(updateHandler : ThreadUpdatedCallback)
    {
        Conversation.callbacks.delete(updateHandler);
    }

    static setChat(chat : Chat) : void
    {
        Conversation.chat = chat;
        Conversation.forceUpdate();

        Conversation.callbacks.forEach(callback => {
            callback();
        });
    }

    static newChat()
    {
        Conversation.chat = undefined;
        Conversation.forceUpdate();
    }


    static setLoading(loading : boolean) : void
    {
        Conversation.loading = loading;
    }

    static getSystemPrompt() : string
    {
        if (Conversation.chat === undefined) {
            return "";
        }
        else {
            return Conversation.chat.getSystemPrompt();
        }
    }

    static setSystemPrompt(prompt : string) : void
    {
        if (Conversation.chat === undefined) {
            Conversation.chat = new Chat(new Message(Role.System, prompt));
        }
        else {
            Conversation.chat.setSystemPrompt(prompt);
        }

        Conversation.forceUpdate();
    }

    static async train(message: Message) : Promise<void>
    {
        if (ModelList.selectedModel === undefined) {
            alert("Select model first");
            return;
        }

        await TrainDialog.showDialog(message, ModelList.selectedModel);
    }

    static async fetchUserCredits(fetch: boolean = false) {
        if (fetch) {
            this.userCredits = await this.api.getCredits!();
            this.notifyCreditListeners();
        }

        return this.userCredits;
    }

    static async fetchTokenPricing() {
        this.tokenPricing = await this.api.getTokenPrices!();
        return this.tokenPricing;
    }

    static subscribeToCredit(listner: (credit: number) => void) {
        this.creditListeners.push(listner);

        return () => {
            this.creditListeners = this.creditListeners.filter(l => l !== listner)
        };
    }

    static notifyCreditListeners() {
        this.creditListeners.forEach(listener => listener(this.userCredits));
    }
};

